/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import type { IconName } from '@/components/Icon';

/** Most stops a page tour may have (GMDS product-tour rule). */
export const MAX_TOUR_STOPS = 6;

export interface TourStep {
  /** Stable id used for keys. */
  id: string;
  /** i18n key for the short heading shown in the tour card. */
  title: string;
  /** i18n key for supporting copy. Keep it to a couple of sentences. */
  body: string;
  /** Glyph in the card header. */
  icon: IconName;
  /** CSS selector of the element the stop points at. */
  selector: string;
  /**
   * The same stop for a layout where `selector` is not on screen (a control that folds into a
   * summary on phones). Used only when the primary target is hidden.
   */
  fallback?: { selector: string; body: string };
}

export type TourId =
  'home' | 'dtcWorkspace' | 'explorer' | 'engineering' | 'analytics' | 'adminUsers';

export interface PageTour {
  id: TourId;
  /** Paths the tour belongs to, matched exactly. */
  routes: string[];
  /**
   * When set, the tour applies only while an element matching this selector is on the page: the
   * DTC workspace opens inside the On-Line / Off-Line lookup routes, which have no tour before it.
   */
  anchor?: string;
  /**
   * i18n keys of the one-time invitation the shell offers. Absent when the page offers its own
   * (the Schematic Explorer has an invitation in its layout).
   */
  invite?: { title: string; body: string };
  /** i18n key of the tour's accessible name. */
  label: string;
  steps: TourStep[];
}

/** `[data-tour="name"]`, the hook every tour target carries. */
export const tourTarget = (name: string): string => `[data-tour="${name}"]`;

const THIS_PAGE = 'tour.invite.thisPage';

const helpStop = (body: string): TourStep => ({
  id: 'help',
  title: 'tour.steps.help.title',
  body,
  icon: 'help',
  selector: tourTarget('help'),
});

/**
 * One contextual tour per page, never more than {@link MAX_TOUR_STOPS} stops. Each points at
 * `data-tour` hooks the page itself carries; a stop whose target is not on screen (the rail and the
 * plant on a phone, the steps pane while a visual tab is open) is skipped when the tour starts.
 */
export const PAGE_TOURS: PageTour[] = [
  {
    id: 'home',
    routes: ['/'],
    invite: { title: 'tour.invite.home.title', body: 'tour.invite.home.body' },
    label: 'tour.button',
    steps: [
      {
        id: 'rail',
        title: 'tour.steps.rail.title',
        body: 'tour.steps.rail.body',
        icon: 'apps',
        selector: tourTarget('rail'),
      },
      {
        id: 'plant',
        title: 'tour.home.plant.title',
        body: 'tour.home.plant.body',
        icon: 'factory',
        selector: tourTarget('plant'),
      },
      {
        id: 'repair-overview',
        title: 'mfg.repairOverview.title',
        body: 'tour.home.repairOverview.body',
        icon: 'history',
        selector: tourTarget('shell-ro'),
      },
      {
        id: 'dtc-search',
        title: 'mfg.home.dtcSearch.title',
        body: 'mfg.home.dtcSearch.subtitle',
        icon: 'warning',
        selector: tourTarget('shell-dtc'),
      },
      {
        id: 'scan',
        title: 'tour.home.scan.title',
        body: 'tour.home.scan.body',
        icon: 'directions_car',
        selector: tourTarget('shell-scan'),
      },
      helpStop('tour.steps.help.body'),
    ],
  },
  {
    id: 'dtcWorkspace',
    routes: ['/online', '/offline', '/diagnostic'],
    anchor: `${tourTarget('mfg-dtcs')}, ${tourTarget('mfg-steps')}`,
    invite: { title: 'tour.invite.dtcWorkspace.title', body: THIS_PAGE },
    label: 'tour.button',
    steps: [
      {
        id: 'dtcs',
        title: 'tour.dtcWorkspace.dtcs.title',
        body: 'tour.dtcWorkspace.dtcs.body',
        icon: 'warning',
        selector: tourTarget('mfg-dtcs'),
      },
      {
        id: 'netlists',
        title: 'tour.dtcWorkspace.netlists.title',
        body: 'tour.dtcWorkspace.netlists.body',
        icon: 'list',
        selector: tourTarget('mfg-netlists'),
      },
      {
        id: 'steps',
        title: 'tour.dtcWorkspace.steps.title',
        body: 'tour.dtcWorkspace.steps.body',
        icon: 'fact_check',
        selector: tourTarget('mfg-steps'),
      },
      {
        id: 'view',
        title: 'tour.dtcWorkspace.view.title',
        body: 'tour.dtcWorkspace.view.body',
        icon: 'view_in_ar',
        selector: tourTarget('mfg-view'),
      },
      {
        id: 'actions',
        title: 'tour.dtcWorkspace.actions.title',
        body: 'tour.dtcWorkspace.actions.body',
        icon: 'check_circle',
        selector: tourTarget('mfg-actions'),
      },
      {
        id: 'assistant',
        title: 'tour.dtcWorkspace.assistant.title',
        body: 'tour.dtcWorkspace.assistant.body',
        icon: 'bot',
        selector: tourTarget('mfg-assistant'),
      },
    ],
  },
  {
    id: 'explorer',
    routes: ['/schematic-explorer'],
    label: 'tour.button',
    steps: [
      {
        id: 'tabs',
        title: 'tour.steps.expTabs.title',
        body: 'tour.steps.expTabs.body',
        icon: 'apps',
        selector: tourTarget('se-tabs'),
      },
      {
        id: 'selection',
        title: 'tour.steps.expSelection.title',
        body: 'tour.steps.expSelection.body',
        icon: 'list',
        selector: tourTarget('se-selection'),
      },
      {
        id: 'canvas',
        title: 'tour.steps.expCanvas.title',
        body: 'tour.steps.expCanvas.body',
        icon: 'schema',
        selector: tourTarget('se-canvas'),
      },
      {
        id: 'viewmode',
        title: 'tour.steps.expViewmode.title',
        body: 'tour.steps.expViewmode.body',
        icon: 'view_in_ar',
        selector: tourTarget('se-viewmode'),
      },
      {
        id: 'vehicle-picker',
        title: 'tour.steps.vehiclePicker.title',
        body: 'tour.steps.vehiclePicker.body',
        icon: 'directions_car',
        selector: tourTarget('vehicle-picker'),
      },
      {
        id: 'mecl',
        title: 'tour.steps.expMecl.title',
        body: 'tour.steps.expMecl.body',
        icon: 'list',
        selector: tourTarget('se-mecl'),
      },
    ],
  },
  {
    id: 'engineering',
    routes: ['/engineering-diagnostics'],
    invite: { title: 'tour.invite.engineering.title', body: THIS_PAGE },
    label: 'tour.button',
    steps: [
      {
        id: 'quick',
        title: 'tour.engineering.quick.title',
        body: 'tour.engineering.quick.body',
        icon: 'search',
        selector: tourTarget('eng-quick'),
      },
      {
        id: 'parts',
        title: 'tour.engineering.parts.title',
        body: 'tour.engineering.parts.body',
        icon: 'memory',
        selector: tourTarget('eng-tile-parts'),
      },
      {
        id: 'failures',
        title: 'tour.engineering.failures.title',
        body: 'tour.engineering.failures.body',
        icon: 'analytics',
        selector: tourTarget('eng-tile-failures'),
      },
      {
        id: 'assistant',
        title: 'tour.engineering.assistant.title',
        body: 'tour.engineering.assistant.body',
        icon: 'bot',
        selector: tourTarget('eng-tile-assistant'),
      },
      helpStop('tour.restart.body'),
    ],
  },
  {
    id: 'analytics',
    routes: ['/analytics'],
    invite: { title: 'tour.invite.analytics.title', body: THIS_PAGE },
    label: 'tour.button',
    steps: [
      {
        id: 'period',
        title: 'tour.analytics.period.title',
        body: 'tour.analytics.period.body',
        icon: 'calendar_month',
        selector: tourTarget('adm-an-period'),
        // Below 600px the period lives behind a one-line summary that opens a sheet.
        fallback: { selector: tourTarget('adm-an-summary'), body: 'tour.analytics.period.summary' },
      },
      {
        id: 'kpis',
        title: 'tour.analytics.kpis.title',
        body: 'tour.analytics.kpis.body',
        icon: 'analytics',
        selector: tourTarget('adm-an-kpis'),
      },
      {
        id: 'tabs',
        title: 'tour.analytics.tabs.title',
        body: 'tour.analytics.tabs.body',
        icon: 'apps',
        selector: tourTarget('adm-an-tabs'),
      },
      {
        id: 'trend',
        title: 'tour.analytics.trend.title',
        body: 'tour.analytics.trend.body',
        icon: 'analytics',
        selector: tourTarget('adm-an-trend'),
      },
      {
        id: 'exclusions',
        title: 'tour.analytics.exclusions.title',
        body: 'tour.analytics.exclusions.body',
        icon: 'person_remove',
        selector: tourTarget('adm-an-exclusions'),
      },
    ],
  },
  {
    id: 'adminUsers',
    routes: ['/admin/users'],
    invite: { title: 'tour.invite.adminUsers.title', body: THIS_PAGE },
    label: 'tour.button',
    steps: [
      {
        id: 'toolnav',
        title: 'nav.admin',
        body: 'tour.adminUsers.toolnav.body',
        icon: 'admin_panel_settings',
        selector: tourTarget('adm-toolnav'),
      },
      {
        id: 'queue',
        title: 'tour.adminUsers.queue.title',
        body: 'tour.adminUsers.queue.body',
        icon: 'how_to_reg',
        selector: tourTarget('adm-usr-queue'),
      },
      {
        id: 'workspaces',
        title: 'tour.adminUsers.workspaces.title',
        body: 'tour.adminUsers.workspaces.body',
        icon: 'apps',
        selector: tourTarget('adm-usr-ws'),
      },
      {
        id: 'role',
        title: 'tour.adminUsers.role.title',
        body: 'tour.adminUsers.role.body',
        icon: 'badge',
        selector: tourTarget('adm-usr-role'),
      },
    ],
  },
];

/** The tour with this id. */
export function tourById(id: TourId): PageTour {
  return PAGE_TOURS.find((tour) => tour.id === id) as PageTour;
}

/**
 * The tour of the page on screen, or null when it has none. Reads the document for tours that
 * need an anchor, so call it after the page has rendered.
 */
export function tourForPath(pathname: string, root: ParentNode = document): PageTour | null {
  return (
    PAGE_TOURS.find(
      (tour) =>
        tour.routes.includes(pathname) && (!tour.anchor || root.querySelector(tour.anchor) != null),
    ) ?? null
  );
}

/** Whether the page on screen has a tour (Help > Quick tour is offered only then). */
export function hasPageTour(pathname: string): boolean {
  return tourForPath(pathname) != null;
}

/**
 * The element `selector` points at, if a user can see it: present, with a size, and not hidden.
 * A rail folded into a drawer, a control inside a collapsed menu or a pane React did not render
 * all answer null.
 */
export function visibleTarget(selector: string): HTMLElement | null {
  const el = document.querySelector<HTMLElement>(selector);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  return getComputedStyle(el).visibility === 'hidden' ? null : el;
}

/** A stop as it will be shown: the target that is on screen and the copy that goes with it. */
export interface ResolvedStep {
  id: string;
  title: string;
  body: string;
  icon: IconName;
  selector: string;
}

/** The stops of `tour` a user can see right now, in order; the rest are skipped. */
export function resolveSteps(tour: PageTour): ResolvedStep[] {
  return tour.steps.flatMap((step): ResolvedStep[] => {
    const { fallback, ...rest } = step;
    if (visibleTarget(step.selector)) return [rest];
    if (fallback && visibleTarget(fallback.selector)) {
      return [{ ...rest, selector: fallback.selector, body: fallback.body }];
    }
    return [];
  });
}

/** Whether any stop of `tour` is on screen (an invitation to a tour with nothing to show is noise). */
export function hasVisibleStop(tour: PageTour): boolean {
  return resolveSteps(tour).length > 0;
}

const seenKey = (id: TourId) => `edx-tour-seen:${id}`;

/** Whether this user was already offered (or took) the tour. */
export function isTourSeen(id: TourId): boolean {
  try {
    return localStorage.getItem(seenKey(id)) === '1';
  } catch {
    return true; // without storage the invitation would come back on every visit
  }
}

/** Remember that the tour was offered, so the invitation shows once. */
export function markTourSeen(id: TourId): void {
  try {
    localStorage.setItem(seenKey(id), '1');
  } catch {
    /* storage unavailable */
  }
}
